import React, { useEffect, useState } from 'react';
import MatchGazeButton from './buttons/MatchGazeButton';
import { useExerciseModels } from './helpers/exercise/useExerciseModels';
import { useGazeMatchState } from './helpers/exercise/useGazeMatchState';
import { MotifColumn, ErrorColumn, RevealedGazeColumn } from './helpers/exercise/GazeViewColumns';
import ExerciseWorkspace from './helpers/exercise/ExerciseWorkspace';
import SelfExpPanel from './helpers/exercise/SelfExpPanel';
import StepGuide, { SIMULATION_STEPS, simulationStep } from './helpers/exercise/StepGuide';

// 視点合わせ演習の画面。状態の編成とレイアウトに徹し、
// レイアウト（左：シミュレーション／右：説明シート）は ExerciseWorkspace、
// カラムの描画は helpers/exercise/GazeViewColumns、
// アニメーションの統括は MatchGazeButton が担う。
// answer / locked: 回答済みの問へ戻ってきたとき。当時の回答を復元表示し、
// 【決定】は押させない（回答は送信済みなので二重送信させない）。
function GazeControl({
  number,
  selectedPart,
  errorGlbPath: errorGlbPathProp,
  isLastQuestion = false,
  answer = null,
  locked = false,
  onAnswered = null, // 【決定】で送信した時点で呼ぶ（親が回答済みとして保存する）
  onComplete,
}) {
  const { errorGlbPath, motifGlbPath } = useExerciseModels(errorGlbPathProp);
  const gaze = useGazeMatchState(errorGlbPath, motifGlbPath);
  // onComplete を渡さない呼び出し（/demo3 の研究発表用デモ）はデモ表示扱い
  const isDemo = typeof onComplete !== 'function';
  // 手順の進み具合：視点合わせを1回見たか／説明シートの空欄をすべて選んだか
  const [sawMatch, setSawMatch] = useState(false);
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    if (gaze.isGazeMatched) setSawMatch(true);
  }, [gaze.isGazeMatched]);

  return (
    <ExerciseWorkspace
      eyebrow="視点変更シミュレーション"
      number={number}
      // 旧：ボタンの動きと目印の説明を長文で出していた。目印の説明は「おかしさが分かる視点」の凡例にある
      instruction={locked
        ? 'この問題は回答済みです。シミュレーションと説明を見直せます。'
        : <StepGuide steps={SIMULATION_STEPS.gaze} current={simulationStep(sawMatch, filled)} done={[sawMatch, filled]} />}
      aside={
        <SelfExpPanel
          number={number}
          phase="gaze-control"
          errorGlbPath={errorGlbPath}
          selectedPart={selectedPart}
          isLastQuestion={isLastQuestion}
          // onComplete を渡さない＝デモ表示。シートは見せるが【決定】は効かせない
          demo={isDemo}
          initialAnswer={answer}
          locked={locked}
          onSelectionChange={(r) => setFilled(Boolean(r.complete))}
          onSubmitted={isDemo ? null : onAnswered}
          onAdvance={isDemo ? null : (selfText, detail) => {
            try { onComplete(selectedPart, selfText, detail); } catch (e) { console.warn('GazeControl:onComplete failed', e); }
          }}
        />
      }
    >
      {/* 3カラム構成なので w-1/3。w-1/2×3 だと合計150%幅になり横スクロールが発生する */}
      <div className="w-full flex flex-row gap-2">
        <MotifColumn motifGlbPath={motifGlbPath} selectedPart={selectedPart} />
        <ErrorColumn
          errorGlbPath={errorGlbPath}
          errorViewGlbPath={gaze.errorViewGlbPath}
          selectedPart={selectedPart}
          errorCameraPos={gaze.errorCameraPos}
          isGazeAnimating={gaze.isGazeAnimating}
          viewerRef={gaze.errorViewerRef}
        />
        <RevealedGazeColumn
          errorGlbPath={errorGlbPath}
          selectedPart={selectedPart}
          showMotifModel={gaze.showMotifModel}
          isGazeAnimating={gaze.isGazeAnimating}
          viewerRef={gaze.wrongViewerRef}
        />
      </div>

      <div className="row-center mt-2">
        <MatchGazeButton
          errorGlbPath={errorGlbPath}
          errorViewerRef={gaze.errorViewerRef}
          wrongViewerRef={gaze.wrongViewerRef}
          isGazeMatched={gaze.isGazeMatched}
          setIsGazeMatched={gaze.setIsGazeMatched}
          setShowMotifModel={gaze.setShowMotifModel}
          setErrorCameraPos={gaze.setErrorCameraPos}
          onAnimatingChange={gaze.setIsGazeAnimating}
        />
      </div>
    </ExerciseWorkspace>
  );
}

export default GazeControl;
